import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Plus,
  Upload,
  Download,
  Code,
  Zap,
  Play,
  CheckCircle2,
  XCircle,
  Clock,
  FileText,
  GitBranch,
  Users,
  Archive,
  GitMerge,
  Lock,
  Filter as FilterIcon,
  Sparkles,
  RefreshCw,
  Eye,
} from "lucide-react";
import { Button } from "../components/ui/button";
import BaselineFormModal from "../components/BaselineFormModal";
import BranchFormModal from "../components/BranchFormModal";
import PushNewDefectModal from "../components/defects/PushNewDefectModal";
import { baselineService } from "../services/baselineService";
import { branchService } from "../services/branchService";
import { projectService } from "../services/projectService";
import { getStoredClientId } from "../utils/tokenUtils";
import { formatDate } from "../utils/formatters";
import toast from "react-hot-toast";
import { logger } from "../services/logService";

// Utility function to format relative dates
const formatRelativeDate = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now - date;
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return "Today";
  if (diffInDays === 1) return "1 day ago";
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 14) return "1 week ago";
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
  if (diffInDays < 60) return "1 month ago";
  if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;
  return `${Math.floor(diffInDays / 365)} years ago`;
};

const BaselineManagementPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const projectId = searchParams.get("project_id");
  const projectName = searchParams.get("project_name") || "";
  const clientId = searchParams.get("client_id") || getStoredClientId();

  const [baselines, setBaselines] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("opened"); // 'opened' or 'all'
  const [showBaselineModal, setShowBaselineModal] = useState(false);
  const [showBranchModal, setShowBranchModal] = useState(false);
  const [editingBaseline, setEditingBaseline] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedBaselineForBranch, setSelectedBaselineForBranch] =
    useState(null);
  const [selectedItem, setSelectedItem] = useState(null); // For right panel
  const [projectData, setProjectData] = useState(null);
  const [showPushDefectModal, setShowPushDefectModal] = useState(false);

  // Load project data
  const loadProjectData = useCallback(async () => {
    if (!projectId || !clientId) return;
    try {
      const response = await projectService.getProjects({
        client_id: clientId,
      });
      const projects = response?.data?.projects || response?.projects || [];
      const project = projects.find((p) => p.project_id === projectId);
      setProjectData(project);
    } catch (error) {
      logger.error(error, "Failed to load project data");
    }
  }, [projectId, clientId]);

  // Load baselines
  const loadBaselines = useCallback(async () => {
    if (!projectId || !clientId) return;

    try {
      setLoading(true);
      const filters = {};
      // Don't filter by status - show all baselines regardless of filter
      // The "opened" filter will be applied in the frontend to show active branches
      // For baselines, we want to show all (draft and published) so users can see newly created ones

      const response = await baselineService.getBaselines(
        projectId,
        clientId,
        filters,
      );
      let baselinesList = [];

      if (response?.message?.data && Array.isArray(response.message.data)) {
        baselinesList = response.message.data;
      } else if (response?.data) {
        if (Array.isArray(response.data)) {
          baselinesList = response.data;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          baselinesList = response.data.data;
        } else if (
          response.data?.baselines &&
          Array.isArray(response.data.baselines)
        ) {
          baselinesList = response.data.baselines;
        }
      } else if (Array.isArray(response)) {
        baselinesList = response;
      }

      setBaselines(baselinesList);
    } catch (error) {
      logger.error(error, "Failed to load baselines");
      toast.error("Failed to load baselines");
      setBaselines([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, clientId, statusFilter]);

  // Load branches for all baselines
  const loadBranches = useCallback(async () => {
    if (!projectId || !clientId) return;

    try {
      const filters = {};
      if (statusFilter === "opened") {
        filters.status = "active"; // Only show active branches for "opened" filter
      }
      // For "all" filter, don't set status filter to show all branches

      const response = await branchService.getBranches(
        projectId,
        clientId,
        null,
        filters,
      );
      let branchesList = [];

      if (response?.message?.data && Array.isArray(response.message.data)) {
        branchesList = response.message.data;
      } else if (response?.data) {
        if (Array.isArray(response.data)) {
          branchesList = response.data;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          branchesList = response.data.data;
        } else if (
          response.data?.branches &&
          Array.isArray(response.data.branches)
        ) {
          branchesList = response.data.branches;
        }
      } else if (Array.isArray(response)) {
        branchesList = response;
      }

      setBranches(branchesList);
    } catch (error) {
      logger.error(error, "Failed to load branches");
      // Don't show error toast, just log it
      setBranches([]);
    }
  }, [projectId, clientId, statusFilter]);

  // Update selectedItem when branches are reloaded
  const selectedItemRef = useRef(selectedItem);
  useEffect(() => {
    selectedItemRef.current = selectedItem;
  }, [selectedItem]);

  useEffect(() => {
    // Update selectedItem if it's a branch to refresh the count
    if (selectedItemRef.current && selectedItemRef.current.type === "branch") {
      const updatedBranch = branches.find(
        (b) => b.branch_id === selectedItemRef.current.branch_id,
      );
      if (updatedBranch) {
        setSelectedItem({ ...updatedBranch, type: "branch" });
      }
    }
  }, [branches]);

  useEffect(() => {
    loadProjectData();
    loadBaselines();
    loadBranches();
  }, [loadProjectData, loadBaselines, loadBranches]);

  // Refresh data when page becomes visible (user returns from creating test cases)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        loadBranches();
        loadBaselines();
      }
    };

    const handleFocus = () => {
      loadBranches();
      loadBaselines();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [loadBranches, loadBaselines]);

  // Group branches under baselines and apply frontend filtering
  const groupedData = baselines
    .map((baseline) => {
      let baselineBranches = branches.filter(
        (branch) => branch.baseline_id === baseline.baseline_id,
      );

      // Apply frontend filter for branches based on statusFilter
      if (statusFilter === "opened") {
        // For "opened" filter, only show baselines that are published or have active branches
        // Also show draft baselines (newly created) so they're visible
        baselineBranches = baselineBranches.filter(
          (branch) => branch.status === "active",
        );
        // If baseline is draft and has no active branches, still show it (newly created)
        if (baseline.status === "draft" && baselineBranches.length === 0) {
          return {
            ...baseline,
            branches: [],
          };
        }
        // If baseline is published or has active branches, show it
        if (baseline.status === "published" || baselineBranches.length > 0) {
          return {
            ...baseline,
            branches: baselineBranches,
          };
        }
        // Otherwise, don't show this baseline for "opened" filter
        return null;
      }

      // For "all" filter, show everything
      return {
        ...baseline,
        branches: baselineBranches,
      };
    })
    .filter((item) => item !== null); // Remove null items

  const totalBaselines = baselines.length;
  const totalBranches = branches.length;

  const handleCreateBaseline = () => {
    setEditingBaseline(null);
    setShowBaselineModal(true);
  };

  const handleForkBranch = () => {
    // Show baseline selection first, then branch creation
    if (baselines.length === 0) {
      toast.error("Please create a baseline first before forking a branch");
      return;
    }

    // Find published baselines
    const publishedBaselines = baselines.filter(
      (b) => b.status === "published",
    );

    if (publishedBaselines.length === 0) {
      toast.error(
        "Please publish a baseline first before forking a branch. Only published baselines can be used to create branches.",
      );
      return;
    }

    // If there's only one published baseline, use it directly
    // Otherwise, use the first one (could be enhanced to show a selection modal)
    const selectedBaseline = publishedBaselines[0];
    setSelectedBaselineForBranch(selectedBaseline);
    setShowBranchModal(true);
  };

  const handleSelectItem = (item, type) => {
    setSelectedItem({ ...item, type }); // type: 'baseline' or 'branch'
  };

  const handlePublishBaseline = async (baseline) => {
    if (
      !window.confirm(
        `Are you sure you want to publish "${baseline.name}"? This will make it immutable and allow branches to be created from it.`,
      )
    ) {
      return;
    }

    try {
      await baselineService.publishBaseline(
        baseline.baseline_id,
        projectId,
        clientId,
        false,
      );
      toast.success("Baseline published successfully!");
      loadBaselines();
      // Update selected item if it's the one we just published
      if (selectedItem?.baseline_id === baseline.baseline_id) {
        const updatedBaseline = { ...baseline, status: "published" };
        setSelectedItem({ ...updatedBaseline, type: "baseline" });
      }
    } catch (error) {
      logger.error(error, "Failed to publish baseline");
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to publish baseline";
      toast.error(errorMessage);
    }
  };

  const handleArchiveBranch = async (branch) => {
    if (!window.confirm(`Are you sure you want to archive "${branch.name}"?`)) {
      return;
    }

    try {
      await branchService.closeBranch(branch.branch_id, projectId, clientId);
      toast.success("Branch archived successfully!");
      loadBranches();
      if (selectedItem?.branch_id === branch.branch_id) {
        setSelectedItem(null);
      }
    } catch (error) {
      logger.error(error, "Failed to archive branch");
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to archive branch";
      toast.error(errorMessage);
    }
  };

  const handleMergeBranch = async (branch) => {
    if (
      !window.confirm(
        `Are you sure you want to merge "${branch.name}" into its baseline?`,
      )
    ) {
      return;
    }

    try {
      await branchService.mergeBranch(branch.branch_id, projectId, clientId);
      toast.success("Branch merged successfully!");
      loadBranches();
      loadBaselines();
      if (selectedItem?.branch_id === branch.branch_id) {
        setSelectedItem(null);
      }
    } catch (error) {
      logger.error(error, "Failed to merge branch");
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to merge branch";
      toast.error(errorMessage);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "published":
      case "merged":
        return <CheckCircle2 className="w-4 h-4 text-green-600" />;
      case "archived":
      case "closed":
        return <XCircle className="w-4 h-4 text-red-600" />;
      case "opened":
      case "active":
      case "draft":
        return <Clock className="w-4 h-4 text-blue-600" />;
      default:
        return <Clock className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      published: "published",
      draft: "opened",
      active: "opened",
      merged: "merged",
      archived: "archived",
      closed: "archived",
    };
    return statusMap[status] || status;
  };

  if (!projectId) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">
            Please select a project to manage baselines.
          </p>
        </div>
      </div>
    );
  }

  const isEmpty = totalBaselines === 0 && totalBranches === 0;

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-900">Baseline</h1>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="text-green-600 border-green-600"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 border-red-600"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-blue-600 border-blue-600"
              >
                <Code className="w-4 h-4 mr-2" />
                Global Variables
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 border-red-600 bg-purple-50"
                onClick={() => setShowPushDefectModal(true)}
                disabled={!clientId || !projectId}
              >
                <Zap className="w-4 h-4 mr-2" />
                Push New Defect
              </Button>
              <Button
                size="sm"
                className="bg-purple-600 text-white hover:bg-purple-700"
              >
                <Play className="w-4 h-4 mr-2" />
                Run Test
              </Button>
            </div>
          </div>

          {/* Project Info and Filters */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-900">
                {projectName || projectData?.name || "Project"} Core
              </span>
              <Lock className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">
                {totalBaselines} Baseline{totalBaselines !== 1 ? "s" : ""},{" "}
                {totalBranches} Branch{totalBranches !== 1 ? "es" : ""}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setStatusFilter("opened")}
                className={`px-3 py-1 text-sm font-medium rounded ${
                  statusFilter === "opened"
                    ? "bg-blue-100 text-blue-700"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                Opened
              </button>
              <button
                onClick={() => setStatusFilter("all")}
                className={`px-3 py-1 text-sm font-medium rounded ${
                  statusFilter === "all"
                    ? "bg-blue-100 text-blue-700"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                All
              </button>
              <button className="p-1 text-gray-600 hover:bg-gray-100 rounded">
                <FilterIcon className="w-4 h-4" />
              </button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCreateBaseline}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Baseline
              </Button>
              <Button
                size="sm"
                className="bg-blue-600 text-white hover:bg-blue-700"
                onClick={handleForkBranch}
              >
                <GitBranch className="w-4 h-4 mr-2" />
                Fork Branch
              </Button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {isEmpty && !loading ? (
            // Empty State
            <div className="flex items-center justify-center h-full">
              <div className="flex gap-8 max-w-4xl">
                {/* Main Empty State Card */}
                <div className="flex-1 flex flex-col items-center justify-center text-center bg-white rounded-lg shadow-sm p-8">
                  <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mb-6">
                    <div className="relative">
                      <FileText className="w-12 h-12 text-gray-400" />
                      <Plus className="w-6 h-6 text-green-600 absolute -bottom-1 -right-1" />
                      <Sparkles className="w-5 h-5 text-purple-600 absolute -top-1 -right-2" />
                    </div>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    No Baseline and Branch yet
                  </h2>
                  <p className="text-gray-600 mb-8 max-w-md">
                    Create baseline and branches to organize the versioning of
                    your test cases.
                  </p>
                  <div className="flex flex-col gap-3 w-full max-w-sm">
                    <Button
                      size="lg"
                      className="w-full bg-purple-600 text-white hover:bg-purple-700"
                      onClick={handleCreateBaseline}
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      Create Baseline
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      className="w-full border-blue-600 text-blue-600 hover:bg-blue-50"
                      onClick={handleForkBranch}
                    >
                      <GitBranch className="w-5 h-5 mr-2" />
                      Fork Branch
                    </Button>
                  </div>
                </div>

                {/* Right Info Card */}
                <div className="w-80 bg-white rounded-lg shadow-sm p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      What are baseline and branch?
                    </h3>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Baseline is a frozen snapshot of approved test cases, while
                    a Branch is an editable copy for making changes without
                    affecting the original set.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            // Hierarchical List View
            <div className="space-y-2">
              {groupedData.map((baseline) => (
                <div
                  key={baseline.baseline_id}
                  className="bg-white rounded-lg border border-gray-200"
                >
                  {/* Baseline Row */}
                  <div
                    className={`p-4 cursor-pointer hover:bg-gray-50 ${
                      selectedItem?.baseline_id === baseline.baseline_id &&
                      selectedItem?.type === "baseline"
                        ? "bg-blue-50 border-l-4 border-l-blue-600"
                        : ""
                    }`}
                    onClick={() => handleSelectItem(baseline, "baseline")}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        {getStatusIcon(baseline.status)}
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-900">
                              Baseline: {baseline.name}
                            </span>
                            <span className="text-sm text-gray-500">
                              #
                              {String(baseline.baseline_id)
                                .slice(-2)
                                .padStart(2, "0")}
                            </span>
                            <span className="text-sm text-gray-600">
                              {getStatusText(baseline.status)}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                            <span>
                              {formatRelativeDate(baseline.created_at)} by{" "}
                              {baseline.created_by_name || "Unknown"}
                            </span>
                            <span>
                              Branch: {baseline.branches?.length || 0}
                            </span>
                            <span>
                              Participant: {baseline.participants_count || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {baseline.status === "draft" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600 border-green-600 hover:bg-green-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePublishBaseline(baseline);
                            }}
                            title="Publish baseline"
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                        )}
                        <GitBranch className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  </div>

                  {/* Branches */}
                  {baseline.branches && baseline.branches.length > 0 && (
                    <div className="border-t border-gray-200 bg-gray-50">
                      {baseline.branches.map((branch) => (
                        <div
                          key={branch.branch_id}
                          className={`pl-12 pr-4 py-3 cursor-pointer hover:bg-gray-100 ${
                            selectedItem?.branch_id === branch.branch_id &&
                            selectedItem?.type === "branch"
                              ? "bg-blue-100 border-l-4 border-l-blue-600"
                              : ""
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectItem(branch, "branch");
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-1">
                              {getStatusIcon(branch.status)}
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-900">
                                    Branch: {branch.name}
                                  </span>
                                  <span className="text-sm text-gray-500">
                                    #
                                    {String(branch.branch_id)
                                      .slice(-2)
                                      .padStart(2, "0")}
                                  </span>
                                  <span className="text-sm text-gray-600">
                                    {getStatusText(branch.status)}
                                  </span>
                                </div>
                                <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                                  {branch.status === "merged" &&
                                    branch.merged_at && (
                                      <span>
                                        was merged on{" "}
                                        {formatDate(branch.merged_at)} by{" "}
                                        {branch.merged_by_name || "Unknown"}
                                      </span>
                                    )}
                                  {branch.status === "archived" &&
                                    branch.closed_at && (
                                      <span>
                                        was archived on{" "}
                                        {formatDate(branch.closed_at)} by{" "}
                                        {branch.closed_by_name || "Unknown"}
                                      </span>
                                    )}
                                  {branch.status === "active" && (
                                    <span>
                                      {formatRelativeDate(branch.created_at)} by{" "}
                                      {branch.created_by_name || "Unknown"}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right Detail Panel */}
      {selectedItem && (
        <div className="w-96 bg-white border-l border-gray-200 overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {selectedItem.name}
                </h2>
                <span className="text-sm text-gray-600">
                  {getStatusText(selectedItem.status)}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  loadBranches();
                  loadBaselines();
                }}
                title="Refresh"
                className="text-gray-600 hover:text-gray-900"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>

            <p className="text-sm text-gray-600 mb-6">
              {selectedItem.description ||
                "Description of the selected " +
                  selectedItem.type +
                  " of 50 char limit."}
            </p>

            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">
                  Type
                </label>
                <p className="text-sm text-gray-900 mt-1 capitalize">
                  {selectedItem.type}
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">
                  Created By
                </label>
                <p className="text-sm text-gray-900 mt-1">
                  {selectedItem.created_by_name || "Unknown"}
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">
                  Created On
                </label>
                <p className="text-sm text-gray-900 mt-1">
                  {selectedItem.created_at
                    ? formatDate(selectedItem.created_at)
                    : "-"}
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">
                  Last Updated
                </label>
                <p className="text-sm text-gray-900 mt-1">
                  {selectedItem.updated_at
                    ? formatDate(selectedItem.updated_at)
                    : "-"}
                </p>
              </div>
              {selectedItem.type === "branch" && (
                <>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase">
                      Associated Branch
                    </label>
                    <p className="text-sm text-gray-900 mt-1">None</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase">
                      Read Only
                    </label>
                    <p className="text-sm text-gray-900 mt-1">No</p>
                  </div>
                </>
              )}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">
                  Total Test Cases
                </label>
                <p className="text-sm text-gray-900 mt-1">
                  {selectedItem.test_cases_count || 0}
                </p>
              </div>
              {selectedItem.type === "baseline" && (
                <>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase">
                      Participant
                    </label>
                    <p className="text-sm text-indigo-600 mt-1 cursor-pointer hover:underline">
                      {selectedItem.participants_count || 0} (linked)
                    </p>
                  </div>
                </>
              )}
              {selectedItem.type === "branch" && (
                <>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase">
                      Test Cases
                    </label>
                    <p className="text-sm text-indigo-600 mt-1 cursor-pointer hover:underline">
                      {selectedItem.test_cases_count || 0} (linked)
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase">
                      Reviewer
                    </label>
                    <p className="text-sm text-gray-900 mt-1">
                      {selectedItem.reviewer_name || "Not assigned"}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase">
                      Assign To
                    </label>
                    <p className="text-sm text-gray-900 mt-1">
                      {selectedItem.assigned_to_name || "Not assigned"}
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Action Buttons */}
            <div className="mt-6 space-y-2">
              {selectedItem.type === "baseline" && (
                <>
                  <Button
                    size="sm"
                    className="w-full bg-indigo-600 text-white hover:bg-indigo-700"
                    onClick={() => {
                      // Navigate to test cases page in view-only mode with baseline_id
                      navigate(
                        `/test-cases?project_id=${projectId}&baseline_id=${selectedItem.baseline_id}&project_name=${encodeURIComponent(projectName)}&client_id=${clientId}&view_only=true`,
                      );
                    }}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View Test Cases
                  </Button>
                  {selectedItem.status === "draft" && (
                    <Button
                      size="sm"
                      className="w-full bg-green-600 text-white hover:bg-green-700"
                      onClick={() => handlePublishBaseline(selectedItem)}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Publish Baseline
                    </Button>
                  )}
                </>
              )}

              {selectedItem.type === "branch" &&
                selectedItem.status === "active" && (
                  <>
                    <Button
                      size="sm"
                      className="w-full bg-blue-600 text-white hover:bg-blue-700"
                      onClick={() => {
                        // Navigate to test cases with branch_id
                        navigate(
                          `/test-cases?project_id=${projectId}&branch_id=${selectedItem.branch_id}&project_name=${encodeURIComponent(projectName)}&client_id=${clientId}`,
                        );
                      }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Test Cases
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full text-red-600 border-red-600 hover:bg-red-50"
                      onClick={() => handleArchiveBranch(selectedItem)}
                    >
                      <Archive className="w-4 h-4 mr-2" />
                      Archive Branch
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full text-green-600 border-green-600 hover:bg-green-50"
                      onClick={() => handleMergeBranch(selectedItem)}
                    >
                      <GitMerge className="w-4 h-4 mr-2" />
                      Merge Branch
                    </Button>
                  </>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <BaselineFormModal
        isOpen={showBaselineModal}
        onClose={() => {
          setShowBaselineModal(false);
          setEditingBaseline(null);
        }}
        onSubmit={() => {
          loadBaselines();
          setShowBaselineModal(false);
        }}
        initialData={editingBaseline}
        projectId={projectId}
        clientId={clientId}
      />

      <BranchFormModal
        isOpen={showBranchModal}
        onClose={() => {
          setShowBranchModal(false);
          setSelectedBaselineForBranch(null);
        }}
        onSubmit={() => {
          loadBranches();
          loadBaselines();
          setShowBranchModal(false);
        }}
        initialData={null}
        projectId={projectId}
        clientId={clientId}
        baselineId={selectedBaselineForBranch?.baseline_id}
      />

      <PushNewDefectModal
        open={showPushDefectModal}
        onClose={() => setShowPushDefectModal(false)}
        clientId={clientId}
        projectId={projectId}
        onSuccess={() => {}}
      />
    </div>
  );
};

export default BaselineManagementPage;
